import { FC, ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import {
  Body1,
  Body2,
  Button,
  Col,
  Content,
  H3,
  H5,
  HSpacer,
  Message,
  Page,
  Row,
  Spacer,
  SpecialContent,
  T,
  colors,
} from '../components'
import { Config } from '../const'
import { loadProperty, saveProperty } from '../helper/LocalStorage'
import { useT } from '../state/language'
import { PlusButton } from '../TimetableList/PlusButton'

export const Overlay = styled.div<{ isVisible?: boolean }>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${colors.zIndex.menu};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  pointer-events: ${(props) => (props.isVisible ? 'inherit' : 'none')};
  transition: opacity 0.3s ease-in-out;
`

const MenuItemInner = styled(H3)<{ isActive?: boolean; isFirst?: boolean }>`
  color: ${colors.Menu.text};
  padding: 1rem;
  color: ${(props) =>
    props.isActive ? colors.Menu.active.text : colors.Menu.text};
  background-color: ${(props) =>
    props.isActive ? colors.Menu.active.bg : 'none'};
  border-top: ${(props) => (props.isFirst && !props.isActive ? 1 : 0)}px solid
    ${colors.Menu.border};
  border-bottom: ${(props) =>
    props.isActive ? undefined : `1px solid ${colors.Menu.border}`};
`

const MenuItem = ({
  to,
  title,
  onClose,
  isFirst,
}: {
  to: string
  title: ReactNode
  onClose: () => void
  isFirst?: boolean
}) => {
  return (
    <NavLink to={to}>
      {({ isActive }) => (
        <MenuItemInner isActive={isActive} isFirst={isFirst} onClick={onClose}>
          {title}
        </MenuItemInner>
      )}
    </NavLink>
  )
}

type Props = {
  onClose: () => void
  isVisible?: boolean
}

let fontSize = loadProperty('fontSize') || 16
document.documentElement.style.fontSize = fontSize + 'px'
const sizeSteps = 2

export const Menu: FC<Props> = ({ onClose, isVisible }) => {
  const t = useT()
  return (
    <Overlay isVisible={isVisible}>
      <Page withBackground center>
        <Content padding="2rem 1rem">
          <Spacer size={1} />

          <MenuItem
            to="/timetable"
            title={<Message id="BUTTON_TIMETABLE" />}
            onClose={onClose}
            isFirst
          />

          <MenuItem
            to="/favorites"
            title={<Message id="BUTTON_FAVORITES" />}
            onClose={onClose}
          />

          {!Config.REACT_APP_SHOW_ARTISTS && (
            <MenuItem
              to="/timetable-flat"
              title={<Message id="BUTTON_TIMETABLE_CLASSIC" />}
              onClose={onClose}
            />
          )}

          {Config.REACT_APP_SHOW_MAP && (
            <>
              <MenuItem
                to="/map"
                title={<Message id="BUTTON_MAP" />}
                onClose={onClose}
              />
            </>
          )}
          {Config.REACT_APP_SHOW_DEPARTURE && (
            <MenuItem
              to="/departure"
              title={<Message id="BUTTON_DEPARTURE" />}
              onClose={onClose}
            />
          )}

          <Spacer size={3} />

          <SpecialContent color={colors.textAlt}>
            <Col gap={5} flexDirection="column">
              <Body1 $textAlign="center">
                <Message id="HELP_HEADLINE" />
              </Body1>

              <H5 $textAlign="center">
                <Message id="HELP_MEDICAL" />
              </H5>

              <Button
                onClick={() => {
                  window.location.href = 'tel:' + t('HELP_MEDICAL_NUMBER')
                }}
              >
                <T id="HELP_MEDICAL_NUMBER" />
              </Button>

              <H5 $textAlign="center">
                <Message id="HELP_OFFICE" />
              </H5>

              <Button
                onClick={() => {
                  window.location.href = 'tel:' + t('HELP_OFFICE_NUMBER')
                }}
              >
                <T id="HELP_OFFICE_NUMBER" />
              </Button>

              <H5 $textAlign="center">
                <Message id="HELP_AWARENESS" />
              </H5>

              <Button
                onClick={() => {
                  window.location.href = 'tel:' + t('HELP_AWARENESS_NUMBER')
                }}
              >
                <T id="HELP_AWARENESS_NUMBER" />
              </Button>
            </Col>
          </SpecialContent>

          <Spacer size={3} />

          {/* <SpecialContent variant="secondary">
            <H5>
              <Message id="SPECIAL_POSTER_HEADLINE" />
            </H5>

            <Text>
              <Message id="SPECIAL_POSTER_TEXT" />
            </Text>
          </SpecialContent> */}

          <Spacer size={3} />

          <Row ml={4} mr={4}>
            <Col flex={1}>
              <Body2 color={colors.textAlt}>
                <Message id="FONT_SIZE" />
              </Body2>
            </Col>
            <HSpacer size={1} />
            <PlusButton
              onClick={() => {
                fontSize += sizeSteps
                saveProperty('fontSize', fontSize)
                document.documentElement.style.fontSize = fontSize + 'px'
              }}
            />
            <HSpacer size={1} />
            <PlusButton
              variant="minus"
              onClick={() => {
                fontSize -= sizeSteps
                saveProperty('fontSize', fontSize)
                document.documentElement.style.fontSize = fontSize + 'px'
              }}
            />
          </Row>

          <Spacer size={6} />
        </Content>
      </Page>
    </Overlay>
  )
}
